import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";

const BranchEdit = () => {
    const toast = useRef(null);
    const navigate = useNavigate();
    const { id } = useParams(); // branch id from URL

    const [clients, setClients] = useState([]);
    const [form, setForm] = useState({
        clientid: null,
        branchName: '',
        branchTitle: '',
        branchSerialNo: '',
        invoicePrefix: '',
        organization: '',
        landPhone: '',
        mobile: '',
        email: '',
        gstin: '',
        address1: '',
        address2: '',
        website: '',
        effectiveFrom: null,
        cinNumber: '',
        pincode: '',
        location: '',
        status: 'Active',
        socialOrderPercent: '',
        dataTransfer: '',
        configStatus: '',
        configDate: null
    });

    useEffect(() => {
        fetchClients();
        fetchBranchDetails();
    }, []);

    // load client list
    const fetchClients = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation');
            const mapped = res.data.map(c => ({
                label: c.client_name,
                value: c.client_id,
                prefix: c.branch_prefix,
                limit: c.no_of_branches
            }));
            setClients(mapped);
        } catch {
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load clients" });
        }
    };

    // load branch details by id
    const fetchBranchDetails = async () => {
        try {
            const res = await axios.get(`https://treatway.co.in/Counter-Backend/api/server_masters/get_branch_by_id/${id}`);

            if (res.data && res.data.status === "success") {
                const branch = res.data.data;

                setForm({
                    clientid: branch.client_id ?? null,
                    branchName: branch.branch_name ?? '',
                    branchTitle: branch.branch_title ?? '',
                    branchSerialNo: branch.branch_slno ?? '',
                    invoicePrefix: branch.invoice_prefix ?? '',
                    organization: branch.org_name ?? '',
                    landPhone: branch.land_no ?? '',
                    mobile: branch.mobile ?? '',
                    email: branch.email_id ?? '',
                    gstin: branch.gstin_no ?? '',
                    address1: branch.address1 ?? '',
                    address2: branch.address2 ?? '',
                    website: branch.website_name ?? '',
                    effectiveFrom: branch.effective_from ? new Date(branch.effective_from.replace(/-/g, '/')) : null,
                    cinNumber: branch.cin_no ?? '',
                    pincode: branch.pincode ?? '',
                    location: branch.location ?? '',
                    status: branch.branch_status ?? 'Active',
                    socialOrderPercent: branch.social_order_percentage ?? '',
                    dataTransfer: branch.data_transfer ?? '',
                    configStatus: branch.config_status ?? '',
                    configDate: branch.config_date && branch.config_date !== '0' ? new Date(branch.config_date.replace(/-/g, '/')) : null
                });
            } else {
                toast.current.show({
                    severity: "warn",
                    summary: "Not Found",
                    detail: res.data.message || "Branch not found"
                });
            }
        } catch (err) {
            console.error(err);
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch branch details"
            });
        }
    };


    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);
    const isValidMobile = (mobile) => /^[0-9]{10}$/.test(mobile);
    const isValidWebsite = (url) =>
        /^(https?:\/\/)?([\w\d\-]+\.)+\w{2,}(\/.*)?$/.test(url);

    const validateForm = () => {
        const requiredFields = [
            { key: 'clientid', label: 'Client' },
            { key: 'branchName', label: 'Branch Name' },
            { key: 'branchSerialNo', label: 'Branch Serial Number' },
            { key: 'invoicePrefix', label: 'Invoice Prefix' },
            { key: 'effectiveFrom', label: 'Effective From' },
            { key: 'status', label: 'Status' },
            { key: 'address1', label: 'Address1' }
        ];

        for (let field of requiredFields) {
            if (!form[field.key]) {
                toast.current.show({
                    severity: "error",
                    summary: "Validation Error",
                    detail: `${field.label} is required`,
                    life: 3000
                });
                return false;
            }
        }

        if (form.gstin && form.gstin.length !== 15) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'GSTIN must be exactly 15 characters',
                life: 3000
            });
            return false;
        }

        if (form.mobile && !isValidMobile(form.mobile)) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Mobile number must be 10 digits',
                life: 3000
            });
            return false;
        }

        if (form.email && !isValidEmail(form.email)) {
            toast.current.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Invalid email address',
                life: 3000
            });
            return false;
        }

        if (form.website && !isValidWebsite(form.website)) {
            toast.current.show({
                severity: "error",
                summary: "Validation Error",
                detail: "Invalid website URL",
                life: 3000
            });
            return false;
        }

        return true;
    };

    const confirmUpdate = () => {
        if (!validateForm()) return;

        confirmDialog({
            message: "Are you sure you want to update this branch?",
            header: "Confirmation",
            icon: "pi pi-exclamation-triangle",
            accept: () => updateBranch()
        });
    };

    const updateBranch = async () => {
        try {
            const formData = new FormData();
            Object.entries(form).forEach(([key, value]) => {
                if (value !== null && value !== "") {
                    if ((key === 'effectiveFrom' || key === 'configDate') && value instanceof Date) {
                        const formattedDate = value.toISOString().split('T')[0];
                        formData.append(key, formattedDate);
                    } else {
                        formData.append(key, value);
                    }
                }
            });

            formData.append("branchId", id);

            const res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/branch_creation', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

            if (res.data.status === "success") {
                toast.current.show({ severity: "success", summary: "Success", detail: res.data.message });
                setTimeout(() => navigate("/branchlist"), 1200);
            } else {
                toast.current.show({ severity: "error", summary: "Error", detail: res.data.message });
            }
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to update branch" });
        }
    };

    return (
        <div className="p-4">
            <ConfirmDialog />
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Edit Branch</h2>

            <div className="card">
                <div className="grid formgrid p-fluid">
                    <div className="field col-12 md:col-6">
                        <label>Client</label>
                        <InputText value={clients.find((c) => c.value === form.clientid)?.label || ''} readOnly />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Organization</label>
                        <InputText value={form.organization} onChange={(e) => handleChange('organization', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Branch Title</label>
                        <InputText value={form.branchTitle} onChange={(e) => handleChange('branchTitle', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Invoice Prefix <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText value={form.invoicePrefix} onChange={(e) => handleChange('invoicePrefix', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Branch Serial Number</label>
                        <InputText value={form.branchSerialNo} readOnly />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Branch Name</label>
                        <InputText value={form.branchName} readOnly />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Land Phone</label>
                        <InputText value={form.landPhone} onChange={(e) => handleChange('landPhone', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Mobile</label>
                        <InputText value={form.mobile} onChange={(e) => handleChange('mobile', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Email</label>
                        <InputText value={form.email} onChange={(e) => handleChange('email', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Website</label>
                        <InputText value={form.website} onChange={(e) => handleChange('website', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Address 1 <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText value={form.address1} onChange={(e) => handleChange('address1', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Address 2</label>
                        <InputText value={form.address2} onChange={(e) => handleChange('address2', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>GSTIN</label>
                        <InputText value={form.gstin} onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>CIN Number</label>
                        <InputText value={form.cinNumber} onChange={(e) => handleChange('cinNumber', e.target.value.toUpperCase())} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Effective From <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Calendar value={form.effectiveFrom} onChange={(e) => handleChange('effectiveFrom', e.value)} showIcon />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>
                            Status <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            value={form.status}
                            options={[
                                { label: 'Active', value: 'Active' },
                                { label: 'InActive', value: 'InActive' }
                            ]}
                            onChange={(e) => handleChange('status', e.value)}
                            placeholder="Select Status"
                        />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Pincode</label>
                        <InputText value={form.pincode} onChange={(e) => handleChange('pincode', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Location</label>
                        <InputText value={form.location} onChange={(e) => handleChange('location', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Social Order Percentage</label>
                        <InputText value={form.socialOrderPercent} onChange={(e) => handleChange('socialOrderPercent', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-6">
                        <label>Data Transfer</label>
                        <InputText value={form.dataTransfer} onChange={(e) => handleChange('dataTransfer', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Config Status</label>
                        <InputText value={form.configStatus} onChange={(e) => handleChange('configStatus', e.target.value)} />
                    </div>

                    <div className="field col-12 md:col-3">
                        <label>Config Date</label>
                        <Calendar value={form.configDate} onChange={(e) => handleChange('configDate', e.value)} showIcon dateFormat="dd-mm-yy" />
                    </div>
                </div>

                <div className="mt-4 flex justify-end">
                    <Button label="Update" icon="pi pi-check" className="p-button-success" onClick={confirmUpdate} />
                </div>
            </div>
        </div>
    );
};

export default BranchEdit;
